/* eslint-disable max-len */
import { gameNamesDesktop } from "abstract/constants/game-ids"
import { Select, Space, Button, Alert, Form, Input } from "antd"
import { FormElement } from "components"
import { FC, useState, useCallback, useMemo } from "react"
import { GamesEditor } from "domains/specialEvents/pages/edit/components/gamesEditor"
import Styles from "./styles.module.css"
import { UpOutlined, DownOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons"

const { Option } = Select

export const GamesSetupTab: FC<{
  selectedGames: string[] | undefined
  setSelectedGames: React.Dispatch<React.SetStateAction<string[] | undefined>>
}> = ({ selectedGames, setSelectedGames }) => {
  const [showAddDropdown, setShowAddDropdown] = useState(false)

  const addGame = useCallback((gameId: string) => {
    setSelectedGames(prev => {
      if (!prev || prev.includes(gameId)) return prev

      return [...prev, gameId]
    })

    setShowAddDropdown(false)
  }, [setSelectedGames])

  const removeGame = useCallback((gameId: string) => {
    setSelectedGames(prev => prev?.filter(id => id !== gameId))
  }, [setSelectedGames])

  const moveGame = useCallback((index: number, direction: "up" | "down") => {
    setSelectedGames(prev => {
      if (!prev) return prev
      const newIndex = direction === "up" ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= prev.length) return prev

      const newGames = [...prev]

      ;[newGames[index], newGames[newIndex]] = [newGames[newIndex], newGames[index]]

      return newGames
    })
  }, [setSelectedGames])

  const availableGames = useMemo(
    () => Object.entries(gameNamesDesktop).filter(
      ([gameId]) => !selectedGames?.includes(gameId),
    ),
    [selectedGames],
  )

  const ArrowButton: FC<{
    direction: "up" | "down"
    onClick: () => void
    disabled: boolean
  }> = ({ direction, onClick, disabled }) => (
    <Button
      type="text"
      size="small"
      icon={direction === "up" ? <UpOutlined /> : <DownOutlined />}
      onClick={onClick}
      disabled={disabled}
      className={Styles.arrowButton}
      style={{ color: disabled ? "#ccc" : "#666" }}
    />
  )

  return (
    <div style={{ background: "#F0F2F5", margin: "-16px", padding: "24px 48px", display: "flex", flexDirection: "column", gap: 45 }}>
      <div className={Styles.sectionWrapper}>
        <div className={Styles.sectionTitle}>Setup</div>
        <div className={Styles.sectionContent}>
          <div className={Styles.halfWidthColumn}>
            Games
            <FormElement type="select" label="" name="selectedGames">
              <div className={Styles.borderedList}>
                {selectedGames?.length ? (
                  <div className={Styles.gamesList}>
                    {selectedGames.map((gameId, index) => (
                      <div key={gameId} className={Styles.gameCard}>
                        <span style={{ flex: 1, fontWeight: 500 }}>{gameNamesDesktop[gameId]}</span>

                        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginRight: "8px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <ArrowButton
                              direction="up"
                              onClick={() => moveGame(index, "up")}
                              disabled={index === 0}
                            />
                            <ArrowButton
                              direction="down"
                              onClick={() => moveGame(index, "down")}
                              disabled={index === selectedGames.length - 1}
                            />
                          </div>
                        </div>

                        <Button
                          type="text"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => removeGame(gameId)}
                          className={Styles.closeButton}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={Styles.emptyState}>
                    No games selected
                  </div>
                )}

                <div style={{ marginTop: selectedGames?.length ? "12px" : "0" }}>
                  {!showAddDropdown ? (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => setShowAddDropdown(true)}
                      style={{ width: "100%", height: "40px" }}
                    >
                      Add Game
                    </Button>
                  ) : (
                    <Select
                      placeholder="Select a game to add"
                      style={{ width: "100%" }}
                      onSelect={addGame}
                      onBlur={() => setShowAddDropdown(false)}
                      open={showAddDropdown}
                      getPopupContainer={trigger => trigger.parentElement}
                      size="large"
                    >
                      {availableGames.map(([gameId, gameName]) => (
                        <Option value={gameId} key={gameId}>
                          {gameName}
                        </Option>
                      ))}
                    </Select>
                  )}
                </div>
              </div>
            </FormElement>
          </div>
        </div>
      </div>
      <div className={Styles.sectionWrapper}>
        <div className={Styles.sectionTitleSmallMargin}>Map Identifier Whitelist (Optional)</div>
        <div className={Styles.helperText}>Leave empty to allow all maps for a game.</div>
        <Alert
          type="warning"
          showIcon
          message="Map identifier filters work only for games that have this feature implemented in the buff.app."
          style={{ marginBottom: "16px" }}
        />
        <div className={Styles.sectionContent}>
          <div className={Styles.halfWidthColumn}>
            {selectedGames?.length ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {selectedGames.map((gameId, index) => (
                  <div
                    key={gameId}
                    className={Styles.gameRow}
                    style={{ borderBottom: index < selectedGames.length - 1 ? "1px solid #f0f0f0" : "none" }}
                  >
                    <span className={Styles.gameLabel}>{gameNamesDesktop[gameId]}</span>
                    <div className={Styles.flex1}>

                      {/* keeping the gameId in form state so mapIdentifiers can be matched by gameId on submit correctly (so we don't have to rely on array index) */}
                      <Form.Item
                        name={["gamesOptions", index, "gameId"]}
                        initialValue={String(gameId)}
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <FormElement
                        type="select"
                        label=""
                        name={["gamesOptions", index, "mapIdentifiers"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select mode="tags" style={{ width: "100%" }} placeholder="Whitelisted map identifiers (optional)" />
                      </FormElement>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={Styles.emptyStateSimple}>
                No games selected.
              </div>
            )}
          </div>
        </div>
      </div>
      <Space direction="vertical" className={Styles.cardContainer} size="large">
        <GamesEditor contestMode />
      </Space>
    </div>
  )
}
